import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  MouseEvent as ReactMouseEvent,
  KeyboardEvent as ReactKeyboardEvent,
} from "react";
import type { IconTypes } from "../../Icons";
import { SelectedIcon } from "../../Icons";
import { MenuButton, MenuDisabledButton, ActionButton } from "./Styles/Buttons";
import { ActionButtonContainer, MenuContainer } from "./Styles/Containers";
import { Menu, Icon, MenuButtonLabel } from "./Styles/Elements";
import {
  sanitiseString,
  concatStringFromClassesArrayList,
} from "../../Utils/StringUtils";

export interface Props {
  text: string;
  menuPosition?: "left" | "right";
  menuWidth?: "wide";
  itemsList: {
    label: string;
    iconType?: IconTypes;
    onClick?: (event: ReactMouseEvent | ReactKeyboardEvent) => void;
    disabled?: boolean;
  }[];
  dataTestId?: string;
  variants?: "default" | "navigation" | "inverted";
  classes?: string;
}

export const ActionMenu = (props: Props) => {
  const classList = {
    defaultClass: "menu-container",
    optionalClasses: [props.classes ? props.classes : null],
  };

  const className = concatStringFromClassesArrayList(classList);

  const sanitisedDataTestId = sanitiseString(props.dataTestId);
  const actionButtonIdentifier = `${sanitisedDataTestId}-button`;

  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const menuContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      menuContainerRef.current &&
      !menuContainerRef.current.contains(event.target as Node)
    )
      handleClose();
  };

  const handleClose = () => {
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  const handleClick = useCallback(
    (event: React.KeyboardEvent | React.MouseEvent, index: number) => {
      event.preventDefault();

      setFocusedIndex(index);
      props.itemsList[index]?.onClick?.(event);
    },
    [props.itemsList]
  );

  const handleFocus = (event: React.KeyboardEvent, direction: number) => {
    const newIndex = focusedIndex + direction;
    if (event.key === "Tab" && newIndex === props.itemsList.length) {
      handleClose();
    }
    if (event.key === "Tab" && event.shiftKey && newIndex === -1) {
      handleClose();
    }

    if (newIndex >= 0 && newIndex < props.itemsList.length) {
      event.preventDefault();
      setFocusedIndex(newIndex);

      document
        .getElementById(
          `${sanitisedDataTestId}-menu-button-${sanitiseString(
            props.itemsList[newIndex].label
          )}-${newIndex}`
        )
        ?.focus();
    }
  };

  const toggleButton = () => {
    if (isOpen) {
      setIsOpen(false);
      setFocusedIndex(-1);
    } else {
      setIsOpen(true);
    }
  };

  const handleActionKeyDown = (event: React.KeyboardEvent) => {
    if (
      !event.shiftKey &&
      (event.key === "Enter" ||
        event.key === "Tab" ||
        event.key === "ArrowDown" ||
        event.key === "Space")
    ) {
      if (!isOpen) {
        event.preventDefault();
        setIsOpen(true);
      } else {
        handleFocus(event, 1);
      }
    } else if (event.shiftKey && event.key === "Tab" && isOpen) {
      handleClose();
    }
  };

  const handleMenuKeyDown = (event: React.KeyboardEvent, index: number) => {
    event.preventDefault();
    event.key === "Enter" && handleClick(event, index);

    if ((!event.shiftKey && event.key === "Tab") || event.key === "ArrowDown") {
      handleFocus(event, 1);
    } else if (
      (event.shiftKey && event.key === "Tab") ||
      event.key === "ArrowUp"
    ) {
      handleFocus(event, -1);
    }
  };

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [isOpen]);

  return (
    <MenuContainer
      ref={menuContainerRef}
      className={className}
      data-testid={`${sanitisedDataTestId}-menu-container`}
    >
      <ActionButtonContainer>
        <ActionButton
          id={actionButtonIdentifier}
          data-testid={actionButtonIdentifier}
          $isOpen={isOpen}
          $isNavigation={props.variants === "navigation" ? true : false}
          $isInverted={props.variants === "inverted"}
          className="ons-btn"
          aria-expanded={isOpen ? "true" : "false"}
          aria-label={`${isOpen ? "Hide" : "Open"} navigation menu`}
          aria-controls="action-menu"
          role="button"
          aria-haspopup="true"
          onClick={toggleButton}
          onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) =>
            handleActionKeyDown(event)
          }
        >
          <span>
            <span className="ons-u-mr-xs">{props.text}</span>
            {isOpen ? (
              <SelectedIcon
                iconType="ArrowIcon"
                style={{ transform: "rotate(-90deg)" }}
              />
            ) : (
              <SelectedIcon
                iconType="ArrowIcon"
                style={{ transform: "rotate(90deg)" }}
              />
            )}
          </span>
        </ActionButton>
      </ActionButtonContainer>

      {isOpen && (
        <Menu
          aria-labelledby="menubutton"
          className="menu"
          role="menu"
          data-testid={`${sanitisedDataTestId}-menu`}
          $menuPosition={props.menuPosition || "left"}
          $menuWidth={props.menuWidth}
        >
          {props.itemsList.map((item, index) => (
            <li key={index} className="ons-u-mb-no" tabIndex={-1}>
              {item.disabled ? (
                <MenuDisabledButton
                  className="ons-btn ons-btn--disabled"
                  $isNavigation={props.variants === "navigation" ? true : false}
                >
                  {item.iconType && (
                    <Icon>
                      <SelectedIcon
                        iconType={item.iconType}
                        classes="ons-icon--l"
                      />
                    </Icon>
                  )}
                  <MenuButtonLabel disabled={item.disabled}>
                    {item.label}
                  </MenuButtonLabel>
                </MenuDisabledButton>
              ) : (
                <MenuButton
                  role="menuitem"
                  className="action-menu-item"
                  tabIndex={index}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) =>
                    handleClick(event, index)
                  }
                  data-testid={`${sanitisedDataTestId}-menu-button-${sanitiseString(
                    item.label
                  )}-${index}`}
                  id={`${sanitisedDataTestId}-menu-button-${sanitiseString(
                    item.label
                  )}-${index}`}
                  onKeyDown={(event: React.KeyboardEvent<HTMLButtonElement>) =>
                    handleMenuKeyDown(event, index)
                  }
                >
                  {item.iconType && (
                    <Icon>
                      <SelectedIcon
                        iconType={item.iconType}
                        classes="ons-icon--l"
                      />
                    </Icon>
                  )}
                  <MenuButtonLabel>{item.label}</MenuButtonLabel>
                </MenuButton>
              )}
            </li>
          ))}
        </Menu>
      )}
    </MenuContainer>
  );
};
